import { CommunicationNotificationItem } from './communication-notification-item';
import { ChatThreadTypeEnum } from 'common-utils';
import { CountItem, MultiCountNotificationItemGroup } from './multi-count-notification-item-group';
import { ChatMessageRI, SectionBasicRI } from 'common-utils/rest-interfaces';
import { BasicThreadContext } from '../interfaces/basic-thread-context';
import { pdsI18n } from '../pds-i18n';
import { NotificationItem } from './notification-item';
import { pdsLogger } from '../pds-logger';

let log = pdsLogger.requiresLogger('notification-service');

export class CommunicationNotificationItemGroup extends MultiCountNotificationItemGroup {
  notifications: Array<CommunicationNotificationItem> = [];
  sectionNames: Array<string> = []; // section names for template rendering

  private sectionMap: Map<string, SectionBasicRI> = new Map();

  constructor(type: string, aggregationKey: string, public userType: string, public currentUserUid: string, isRead = true, isSeen = true) {
    super(type, aggregationKey, false, isRead, isSeen);
  }

  get imageURL(): string {  // this will need to be updated once we add new thread types
    if (!Array.isArray(this.notifications) || this.notifications.length === 0) {
      throw 'tried to get imageURL for group with no notifications';
    }
    let uid: string = this.notifications[0].getTopicOrOwnerUid();
    if (this.userType === 'TEACHER') {
      return `/images/student-${uid}`;
    } else {
      return `/images/teacher-${uid}`;
    }
  }

  get countItems(): Array<CountItem> {
    let unread: Array<CommunicationNotificationItem> = <Array<CommunicationNotificationItem>>this.getUnreadNotifications();
    if (this.userType === 'TEACHER') {
      let studentCount = unread.filter((notification) => {
        return notification.getThreadType() === ChatThreadTypeEnum.STUDENT_TEACHER;
      }).length;

      let parentCount = unread.filter((notification) => {
        return notification.getThreadType() === ChatThreadTypeEnum.TEACHER_PARENT;
      }).length;

      return [
        { iconName: 'notifications-student', count: studentCount, labelText: pdsI18n.getText('pds|chat notifications|xx Unread Notifications From Student', { count: studentCount }) },
        { iconName: 'notifications-parent', count: parentCount, labelText: pdsI18n.getText('pds|chat notifications|xx Unread Notifications From Parent', { count: parentCount }) }
      ];
    } else {
      return [
        { iconName: null, count: unread.length, labelText: pdsI18n.getText('pds|chat notifications|xx Unread Notifications From Teacher', { count: unread.length }) }
      ];
    }
  }

  addNotification(notification: NotificationItem): void {
    super.addNotification(notification);
    log(`Adding communication notification: ${JSON.stringify(notification)}`);

    let sections: Array<SectionBasicRI> = (<CommunicationNotificationItem>notification).getSections();
    for (let section of sections) {
      this.sectionMap.set(section.uid, section);
    }
    this.sectionNames = Array.from(this.sectionMap.values()).map((section: SectionBasicRI) => section.courseName);
  }

  get messages(): Array<ChatMessageRI> {
    return this.getUnreadNotifications().map((notification) => {
      return (<CommunicationNotificationItem>notification).message;
    });
  }

  get sectionUids(): Array<string> {
    return Array.from(this.sectionMap.keys());
  }

  getThreadContext(): BasicThreadContext {
    let unreadNotifications: Array<NotificationItem> = this.getUnreadNotifications();
    let hasStudentNotifications: boolean = unreadNotifications.some((notification: CommunicationNotificationItem) => {
      return notification.getThreadType() === ChatThreadTypeEnum.STUDENT_TEACHER;
    });

    let threadType: ChatThreadTypeEnum = hasStudentNotifications ? ChatThreadTypeEnum.STUDENT_TEACHER : ChatThreadTypeEnum.TEACHER_PARENT;

    let notification: CommunicationNotificationItem | undefined = <CommunicationNotificationItem>unreadNotifications.find((notification: CommunicationNotificationItem) => {
      return notification.getThreadType() === threadType;
    });

    if (notification === null) {
      throw 'thread type not available when it should be';
    }

    return { threadUid: notification.threadUid, threadType, topicUid: notification.getTopicUid() };
  }

  get lookUpKey(): string {
    if (this.notifications.length === 0) {
      throw 'can not get look up key of empty group this should not have happened.';
    }
    return this.notifications[0].lookUpKey;
  }
}
